import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Button,
  Paper,
  Box,
  Typography,
  Link,
  Stack,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationSchema } from './validate';
import { useThemeMode } from '../../utils/useThemeMode';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import googleTileIcon from 'icons/google-tile.svg';

export const SignUpForm = ({ onSubmit, errorMessage }) => {
  const { mode } = useThemeMode();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);
  
  // React logo blue color for accents
  const reactBlue = 'rgba(97, 218, 251, 0.9)';
  
  // Dynamic form styling based on theme
  const formStyle = {
    background: mode === 'dark' ? '#0A1929' : '#fff',
    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'inherit',
    borderRadius: '15px',
    padding: '30px',
    boxShadow: mode === 'dark' 
      ? `0px 0px 10px rgba(97, 218, 251, 0.2)` 
      : '0px 0px 10px rgba(0, 0, 0, 0.1)',
    border: mode === 'dark' ? `1px solid rgba(97, 218, 251, 0.1)` : 'none',
    width: '100%',
    overflow: 'hidden',
    position: 'relative'
  };

  const textFieldStyle = {
    marginBottom: '20px',
    '& .MuiInputBase-input': { 
      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'inherit' 
    },
    '& .MuiInputLabel-root': { 
      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
      '&.Mui-focused': {
        color: mode === 'dark' ? reactBlue : undefined
      }
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: mode === 'dark' ? 'rgba(97, 218, 251, 0.3)' : undefined
      },
      '&:hover fieldset': {
        borderColor: mode === 'dark' ? 'rgba(97, 218, 251, 0.5)' : undefined
      },
      '&.Mui-focused fieldset': {
        borderColor: mode === 'dark' ? reactBlue : undefined
      }
    },
    '& .MuiFormLabel-root': {
      background: mode === 'dark' ? '#0A1929' : '#fff',
      padding: '0 5px'
    }
  };

  const buttonStyle = {
    marginTop: '20px',
    padding: '12px 20px',
    fontSize: '1rem',
    backgroundColor: mode === 'dark' ? reactBlue : '#1976d2',
    '&:hover': {
      backgroundColor: mode === 'dark' ? 'rgba(97, 218, 251, 0.8)' : '#1565c0'
    },
    boxShadow: mode === 'dark' ? '0 4px 12px rgba(97, 218, 251, 0.25)' : undefined,
    transition: 'all 0.2s ease-in-out',
    textTransform: 'none',
    fontWeight: 'bold'
  };

  const googleButtonStyle = {
    marginTop: '10px',
    padding: '12px 20px',
    fontSize: '1rem',
    backgroundColor: mode === 'dark' ? '#333' : '#f5f5f5',
    color: mode === 'dark' ? '#fff' : 'rgba(0, 0, 0, 0.87)',
    '&:hover': {
      backgroundColor: mode === 'dark' ? '#444' : '#e0e0e0'
    },
    transition: 'all 0.2s ease-in-out',
    textTransform: 'none',
    border: mode === 'dark' ? '1px solid #555' : '1px solid #ccc',
  };

  const linkStyle = {
    color: mode === 'dark' ? reactBlue : '#1976d2',
    '&:hover': {
      color: mode === 'dark' ? 'rgba(97, 218, 251, 0.8)' : '#1565c0'
    },
    textDecoration: 'none'
  };

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      passwordRepeat: ''
    },
    mode: 'onChange',
    resolver: yupResolver(validationSchema)
  });

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowPasswordRepeat = () => {
    setShowPasswordRepeat(!showPasswordRepeat);
  };

  return (
    <Box component={Paper} elevation={3} sx={formStyle}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First Name"
                  variant="outlined"
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  sx={textFieldStyle}
                />
              )}
            />

            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last Name"
                  variant="outlined"
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  sx={textFieldStyle}
                />
              )}
            />
          </Box>

          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                variant="outlined"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={textFieldStyle}
              />
            )}
          />

          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                error={!!errors.password}
                helperText={errors.password?.message}
                sx={textFieldStyle}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        edge="end"
                        sx={{ color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : undefined }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            )}
          />

          <Controller
            name="passwordRepeat"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Confirm Password"
                type={showPasswordRepeat ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                error={!!errors.passwordRepeat}
                helperText={errors.passwordRepeat?.message}
                sx={textFieldStyle}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPasswordRepeat}
                        edge="end"
                        sx={{ color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : undefined }}
                      >
                        {showPasswordRepeat ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            )}
          />

          {errorMessage && (
            <Typography 
              color="error" 
              align="center"
              sx={{ 
                padding: '10px',
                backgroundColor: mode === 'dark' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(211, 47, 47, 0.05)',
                borderRadius: '4px',
                marginY: '10px'
              }}
            >
              {errorMessage}
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={buttonStyle}
          >
            Create Account
          </Button>
          
          <Button
            variant="outlined"
            fullWidth
            sx={googleButtonStyle}
            onClick={() => window.location.href = '/auth/google'}
          >
            <img 
              src={googleTileIcon} 
              alt="Google logo" 
              style={{ height: '18px', marginRight: '10px' }} 
            />
            Sign up with Google
          </Button>

          <Divider sx={{ 
            my: 2, 
            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : undefined,
            '&::before, &::after': {
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : undefined,
            }
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'text.secondary',
                px: 1,
                backgroundColor: mode === 'dark' ? '#0A1929' : '#fff',
              }}
            >
              or
            </Typography>
          </Divider>

          <Typography align="center" variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'text.secondary' }}>
            Already have an account?{' '}
            <Link 
              component={RouterLink} 
              to="/login"
              sx={linkStyle}
            >
              Log in
            </Link>
          </Typography>
        </Stack>
      </form>
    </Box>
  );
};

SignUpForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  errorMessage: PropTypes.string
};

export default SignUpForm;